'use client';
import React from 'react';
import { MessageSquare, Home, CheckSquare, Mail, Calendar, Users, Sparkles, Brain } from 'lucide-react';
import Link from 'next/link';

const memoryItems = [
  { icon: MessageSquare, label: 'Every conversation' },
  { icon: Users, label: 'Every client' },
  { icon: Home, label: 'Every showing' },
  { icon: CheckSquare, label: 'Every promise' },
  { icon: CheckSquare, label: 'Every task' },
  { icon: Mail, label: 'Every email' },
  { icon: Calendar, label: 'Every interaction' },
];

export default function BenefitsSection() {
  return (
    <section id="memory" className="py-24 relative overflow-hidden" style={{ background: '#0B0F19' }}>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 40% at 70% 40%, rgba(167,139,250,0.05) 0%, transparent 70%)' }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div>
            <div className="section-badge mb-6">
              <Brain size={12} />
              Memory Is the Differentiator
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-6"
              style={{ color: '#F0F4FF' }}
            >
              The AI that never{' '}
              <span className="gradient-cyan-lavender-text">forgets.</span>
            </h2>
            <p className="text-lg leading-relaxed mb-8" style={{ color: 'rgba(240,244,255,0.60)' }}>
              Most tools start from scratch every time you open them. AgentOS builds persistent memory across your entire business. The longer you use it, the smarter it gets.
            </p>
            <p className="text-base leading-relaxed mb-8" style={{ color: 'rgba(240,244,255,0.50)' }}>
              Never lose context again. Never repeat yourself. Never wonder what you told a client six months ago. AgentOS holds the full picture — so you can focus on closing.
            </p>
            <div
              className="rounded-2xl p-6"
              style={{
                background: 'rgba(167,139,250,0.08)',
                border: '1px solid rgba(167,139,250,0.20)',
              }}
            >
              <p className="text-base font-semibold italic" style={{ color: '#F0F4FF' }}>
                "Your AI business partner remembers everything — so you don't have to."
              </p>
            </div>
          </div>

          {/* Right: Memory Visual */}
          <div
            className="rounded-3xl p-8 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(167,139,250,0.08) 0%, rgba(20,26,46,0.95) 100%)',
              border: '1px solid rgba(167,139,250,0.20)',
              boxShadow: '0 8px 40px rgba(167,139,250,0.10)',
            }}
          >
            <div
              className="absolute top-0 right-0 w-64 h-64 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.15) 0%, transparent 70%)', filter: 'blur(30px)' }}
            />
            <div className="relative">
              <div className="flex items-center gap-3 mb-8">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #A78BFA, #7C3AED)', boxShadow: '0 4px 20px rgba(167,139,250,0.30)' }}
                >
                  <Brain size={22} className="text-white" />
                </div>
                <div>
                  <h3 className="font-extrabold text-lg" style={{ color: '#F0F4FF' }}>Persistent Business Memory</h3>
                  <p className="text-sm" style={{ color: 'rgba(240,244,255,0.45)' }}>Always on. Always learning.</p>
                </div>
              </div>

              <div className="space-y-3">
                {memoryItems?.map((item, i) => (
                  <div
                    key={item?.label}
                    className="flex items-center gap-4 rounded-xl px-4 py-3"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.07)',
                      animationDelay: `${i * 0.1}s`,
                    }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.20)' }}
                    >
                      <item.icon size={14} style={{ color: '#A78BFA' }} />
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'rgba(240,244,255,0.75)' }}>{item?.label}</span>
                    <div className="ml-auto flex items-center gap-1">
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#A78BFA' }} />
                      <span className="text-xs" style={{ color: 'rgba(167,139,250,0.60)', fontFamily: 'JetBrains Mono, monospace' }}>remembered</span>
                    </div>
                  </div>
                ))}
              </div>

              <div
                className="mt-6 rounded-xl p-4 text-center"
                style={{ background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.20)' }}
              >
                <p className="text-sm font-semibold" style={{ color: '#A78BFA' }}>
                  <Sparkles size={12} className="inline mr-1.5" />
                  Context builds over time. The more you use it, the smarter it gets.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* See in Action link */}
        <div className="mt-12 text-center">
          <Link
            href="/in-action"
            className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
            style={{ color: 'rgba(0,212,255,0.70)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#00D4FF'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(0,212,255,0.70)'; }}
          >
            See how memory works throughout your day →
          </Link>
        </div>
      </div>
    </section>
  );
}
